/**
 * Note / chord pattern → MIDI sequence for the tracker Generate Notes tool.
 *
 * Tokens are space-separated. Octave sticks after `:n` until overridden:
 *   `C Cm CM7:4 Dm7 G7`
 * Chord qualities use closed root-position voicings (tracker fills are mono / arpeggiated).
 */

export type ArpMode =
  | "up"
  | "down"
  | "up-down"
  | "down-up"
  | "as-played"
  | "random";

export const ARP_MODES: readonly ArpMode[] = [
  "as-played",
  "up",
  "down",
  "up-down",
  "down-up",
  "random",
] as const;

export type ParseGenerateNotesResult =
  | { ok: true; notes: number[]; warnings: string[] }
  | { ok: false; error: string; notes: number[]; warnings: string[] };

const NOTE_PC: Record<string, number> = {
  C: 0,
  D: 2,
  E: 4,
  F: 5,
  G: 7,
  A: 9,
  B: 11,
};

/** Longest-first quality → semitone intervals from root. */
const QUALITY_INTERVALS: Array<[string, number[]]> = [
  ["maj13", [0, 4, 7, 11, 14, 21]],
  ["maj11", [0, 4, 7, 11, 14, 17]],
  ["maj9", [0, 4, 7, 11, 14]],
  ["maj7", [0, 4, 7, 11]],
  ["maj6", [0, 4, 7, 9]],
  ["mmaj7", [0, 3, 7, 11]],
  ["mmaj9", [0, 3, 7, 11, 14]],
  ["minmaj7", [0, 3, 7, 11]],
  ["halfdim7", [0, 3, 6, 10]],
  ["halfdim", [0, 3, 6, 10]],
  ["m7b5", [0, 3, 6, 10]],
  ["m9b5", [0, 3, 6, 10, 14]],
  ["dim7", [0, 3, 6, 9]],
  ["dim9", [0, 3, 6, 9, 14]],
  ["aug7", [0, 4, 8, 10]],
  ["aug9", [0, 4, 8, 10, 14]],
  ["sus2", [0, 2, 7]],
  ["sus4", [0, 5, 7]],
  ["add9", [0, 4, 7, 14]],
  ["add11", [0, 4, 7, 17]],
  ["add2", [0, 2, 4, 7]],
  ["add4", [0, 4, 5, 7]],
  ["7b9", [0, 4, 7, 10, 13]],
  ["7#9", [0, 4, 7, 10, 15]],
  ["7#11", [0, 4, 7, 10, 18]],
  ["7b13", [0, 4, 7, 10, 20]],
  ["7#5", [0, 4, 8, 10]],
  ["7b5", [0, 4, 6, 10]],
  ["9#11", [0, 4, 7, 10, 14, 18]],
  ["m13", [0, 3, 7, 10, 14, 21]],
  ["m11", [0, 3, 7, 10, 14, 17]],
  ["m9", [0, 3, 7, 10, 14]],
  ["m7", [0, 3, 7, 10]],
  ["m6", [0, 3, 7, 9]],
  ["min7", [0, 3, 7, 10]],
  ["min9", [0, 3, 7, 10, 14]],
  ["min6", [0, 3, 7, 9]],
  ["min", [0, 3, 7]],
  ["dim", [0, 3, 6]],
  ["aug", [0, 4, 8]],
  ["dom7", [0, 4, 7, 10]],
  ["sus", [0, 5, 7]],
  ["13", [0, 4, 7, 10, 14, 21]],
  ["11", [0, 4, 7, 10, 14, 17]],
  ["9", [0, 4, 7, 10, 14]],
  ["7", [0, 4, 7, 10]],
  ["6", [0, 4, 7, 9]],
  ["5", [0, 7]],
  // Short aliases after longer forms
  ["M13", [0, 4, 7, 11, 14, 21]],
  ["M11", [0, 4, 7, 11, 14, 17]],
  ["M9", [0, 4, 7, 11, 14]],
  ["M7", [0, 4, 7, 11]],
  ["M6", [0, 4, 7, 9]],
  ["mM7", [0, 3, 7, 11]],
  ["Maj7", [0, 4, 7, 11]],
  ["Maj9", [0, 4, 7, 11, 14]],
  ["Maj", [0, 4, 7]],
  ["maj", [0, 4, 7]],
  ["M", [0, 4, 7]],
  ["m", [0, 3, 7]],
  ["o", [0, 3, 6]],
  ["ø", [0, 3, 6, 10]],
  ["+", [0, 4, 8]],
  ["-", [0, 3, 7]],
];

const TOKEN_RE = /^([A-Ga-g])([#b]?)(.*)$/;

function clampMidi(midi: number): number {
  return Math.max(0, Math.min(127, Math.round(midi)));
}

function rootMidi(pc: number, octave: number): number {
  return clampMidi((octave + 1) * 12 + pc);
}

function normalizeQualityKey(raw: string): string {
  return raw.trim();
}

function lookupIntervals(qualityRaw: string): number[] | undefined {
  const q = normalizeQualityKey(qualityRaw);
  if (!q) {
    return [0];
  }
  for (const [key, intervals] of QUALITY_INTERVALS) {
    if (key === q) {
      return intervals;
    }
  }
  const lower = q.toLowerCase();
  for (const [key, intervals] of QUALITY_INTERVALS) {
    // Keep M vs m / + vs case-fold collisions exact-only.
    if (key.length <= 1) {
      continue;
    }
    if (key.toLowerCase() === lower) {
      return intervals;
    }
  }
  return undefined;
}

/**
 * Expand one token at `octave` into MIDI notes (closed voicing, ascending).
 * Returns undefined when the token is invalid.
 */
export function expandGenerateToken(
  token: string,
  octave: number,
): { midis: number[]; octave: number } | undefined {
  const raw = token.trim();
  if (!raw) {
    return undefined;
  }

  let body = raw;
  let nextOctave = octave;
  const colon = raw.lastIndexOf(":");
  if (colon >= 0) {
    const maybeOct = raw.slice(colon + 1);
    if (/^-?\d+$/.test(maybeOct)) {
      nextOctave = Number(maybeOct);
      body = raw.slice(0, colon);
    }
  }

  const match = TOKEN_RE.exec(body);
  if (!match) {
    return undefined;
  }
  const letter = match[1]!.toUpperCase();
  const accidental = match[2] ?? "";
  const quality = match[3] ?? "";
  const natural = NOTE_PC[letter];
  if (natural === undefined) {
    return undefined;
  }
  let pc = natural;
  if (accidental === "#") {
    pc += 1;
  } else if (accidental === "b") {
    pc -= 1;
  }
  pc = ((pc % 12) + 12) % 12;

  const intervals = lookupIntervals(quality);
  if (!intervals) {
    return undefined;
  }

  const root = rootMidi(pc, nextOctave);
  const midis = intervals.map(semi => clampMidi(root + semi));
  return { midis, octave: nextOctave };
}

/** Parse a full space-separated pattern into a flat as-played MIDI list. */
export function parseGenerateNotesPattern(
  pattern: string,
  defaultOctave = 4,
): ParseGenerateNotesResult {
  const warnings: string[] = [];
  const parts = pattern.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) {
    return { ok: false, error: "Enter at least one note or chord.", notes: [], warnings };
  }

  let octave = defaultOctave;
  const notes: number[] = [];
  for (const part of parts) {
    const expanded = expandGenerateToken(part, octave);
    if (!expanded) {
      return {
        ok: false,
        error: `Unrecognized token “${part}”.`,
        notes,
        warnings,
      };
    }
    octave = expanded.octave;
    notes.push(...expanded.midis);
  }

  if (notes.length === 0) {
    return { ok: false, error: "Pattern produced no notes.", notes, warnings };
  }
  return { ok: true, notes, warnings };
}

function bounceExclusive(ordered: number[]): number[] {
  if (ordered.length <= 1) {
    return ordered.slice();
  }
  const up = ordered;
  const down = ordered.slice(0, -1).reverse().slice(0, -1);
  return [...up, ...down];
}

/** Reorder / expand a MIDI list for classic arpeggiator modes. */
export function applyArpMode(
  notes: number[],
  mode: ArpMode,
  random: () => number = Math.random,
): number[] {
  if (notes.length <= 1) {
    return notes.slice();
  }

  switch (mode) {
    case "as-played":
      return notes.slice();
    case "up": {
      return [...notes].sort((a, b) => a - b);
    }
    case "down": {
      return [...notes].sort((a, b) => b - a);
    }
    case "up-down": {
      const up = [...notes].sort((a, b) => a - b);
      return bounceExclusive(up);
    }
    case "down-up": {
      const down = [...notes].sort((a, b) => b - a);
      return bounceExclusive(down);
    }
    case "random": {
      const out = notes.slice();
      for (let i = out.length - 1; i > 0; i--) {
        const j = Math.floor(random() * (i + 1));
        const tmp = out[i]!;
        out[i] = out[j]!;
        out[j] = tmp;
      }
      return out;
    }
    default:
      return notes.slice();
  }
}

/**
 * Place notes into `rowCount` rows with `gap` empty rows between successive hits.
 * Empty slots are `null` (clear the note field). Sequence repeats to fill capacity.
 */
export function placeNotesInRows(
  notes: number[],
  rowCount: number,
  gap: number,
): Array<number | null> {
  const rows = Math.max(0, Math.floor(rowCount));
  const out: Array<number | null> = Array.from({ length: rows }, () => null);
  if (rows === 0 || notes.length === 0) {
    return out;
  }
  const stride = Math.max(0, Math.floor(gap)) + 1;
  let noteIndex = 0;
  for (let row = 0; row < rows; row += stride) {
    out[row] = notes[noteIndex % notes.length]!;
    noteIndex += 1;
  }
  return out;
}

/** Full pipeline: pattern → arp → row fill. */
export function buildGeneratedRows(options: {
  pattern: string;
  mode: ArpMode;
  gap: number;
  rowCount: number;
  defaultOctave?: number;
  random?: () => number;
}): ParseGenerateNotesResult & { rows?: Array<number | null> } {
  const parsed = parseGenerateNotesPattern(options.pattern, options.defaultOctave ?? 4);
  if (!parsed.ok) {
    return parsed;
  }
  const sequenced = applyArpMode(parsed.notes, options.mode, options.random);
  const rows = placeNotesInRows(sequenced, options.rowCount, options.gap);
  return { ...parsed, notes: sequenced, rows };
}
